// src/contexts/ThemeContext.jsx
// Adicionado: animação de transição ao trocar tema

import React, {
  createContext, useContext, useEffect, useState, useCallback,
} from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { buildGymCssVars, injectCssVars, clearGymCssVars } from '../utils/themeColorUtils';
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';

const STORAGE_KEY  = 'lesvita_theme_mode';
const DEFAULT_MODE = 'dark'; // ← dark como padrão

const ThemeContext = createContext(null);

// ─── Animação de transição de tema ────────────────────────────────────────────
// Cria um ripple circular que se expande a partir do ponto clicado
// e troca o tema no pico da animação — efeito de "revelar" o novo tema.

const TRANSITION_MS = 380; // duração total da animação

function animateThemeChange(originX, originY, toMode) {
  const root = document.documentElement;

  // ── 1. Overlay que cobre a tela ──────────────────────────────────────────
  const overlay = document.createElement('div');
  const bgColor = toMode === 'dark' ? '#08081A' : '#F5F7FA';

  Object.assign(overlay.style, {
    position:        'fixed',
    inset:           '0',
    zIndex:          '99999',
    pointerEvents:   'none',
    background:      bgColor,
    opacity:         '0',
    transition:      `opacity ${TRANSITION_MS * 0.5}ms ease`,
  });
  document.body.appendChild(overlay);

  // ── 2. Ripple circular a partir do ponto de clique ───────────────────────
  const ripple = document.createElement('div');
  const maxR   = Math.hypot(
    Math.max(originX, window.innerWidth  - originX),
    Math.max(originY, window.innerHeight - originY),
  ) * 2.2; // garante cobrir toda a tela


  document.body.appendChild(ripple);

  // ── 3. Ativa transição suave nos elementos ────────────────────────────────
  root.classList.add('theme-transition');

  // RAF garante que o browser aplica os estilos iniciais antes de animar
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // Expande o ripple
      ripple.style.width  = `${maxR}px`;
      ripple.style.height = `${maxR}px`;
    });
  });

  // ── 4. Troca o tema no meio da animação ───────────────────────────────────
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(); // o caller faz setMode() aqui
    }, TRANSITION_MS * 0.45);

    // ── 5. Limpa overlay + ripple + classe ───────────────────────────────────
    setTimeout(() => {
      ripple.style.opacity = '0';
      overlay.style.opacity = '0';
      setTimeout(() => {
        ripple.remove();
        overlay.remove();
        root.classList.remove('theme-transition');
      }, 160);
    }, TRANSITION_MS);
  });
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'dark' || saved === 'light') return saved;
    } catch (_) {}
    return DEFAULT_MODE;
  });

  const [brand,      setBrand]      = useState('lesvita');
  const [gymData,    setGymData]    = useState(null);
  const [gymLoading, setGymLoading] = useState(false);

  // Aplica data-theme e data-brand no <html>
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-theme', mode);
    html.setAttribute('data-brand', brand);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch (_) {}
    // No Android a WebView fica por baixo da barra de status (MainActivity),
    // então quem está atrás dos ícones do sistema é o fundo do APP, não o do
    // sistema. Sem isto o estilo DEFAULT segue o modo escuro do aparelho:
    // tema claro no app + sistema escuro = ícones brancos sobre fundo claro.
    // iOS não entra: lá o comportamento atual está certo e não muda.
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      SystemBars.setStyle({
        style: mode === 'dark' ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
      }).catch(() => {});
    }
  }, [mode, brand]);

  // ── Toggle com animação ───────────────────────────────────────────────────
  const toggleMode = useCallback((event) => {
    // Usa a posição do clique como origem do ripple;
    // fallback para o centro da tela se não vier de um evento
    const x = event?.clientX ?? window.innerWidth  / 2;
    const y = event?.clientY ?? window.innerHeight / 2;

    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      animateThemeChange(x, y, next).then(() => setMode(next));
      return prev; // mantém o valor atual até a animação chegar ao meio
    });
  }, []);

  // ── Gym theme (inalterado) ────────────────────────────────────────────────
  const loadGymTheme = useCallback(async (gymId) => {
    if (!gymId) return;
    setGymLoading(true);
    try {
      const snap = await getDoc(doc(db, 'ACADEMIA', gymId));
      if (!snap.exists()) {
        console.warn('[ThemeContext] Academia não encontrada:', gymId);
        return;
      }
      const data    = snap.data();
      const primary = data.primaryColor || data.theme_primary || '#0033CC';
      const accent  = data.accentColor  || data.theme_accent  || null;
      injectCssVars(buildGymCssVars(primary, accent));
      setGymData({
        id:           gymId,
        name:         data.name || data.nome || '',
        logoUrl:      data.logoUrl || data.logo_url || null,
        primaryColor: primary,
        accentColor:  accent,
      });
      setBrand('gym');
    } catch (err) {
      console.error('[ThemeContext] Erro ao carregar tema da academia:', err);
    } finally {
      setGymLoading(false);
    }
  }, []);

  const clearGymTheme = useCallback(() => {
    clearGymCssVars();
    setGymData(null);
    setBrand('lesvita');
  }, []);

  return (
    <ThemeContext.Provider value={{
      mode,
      brand,
      gymData,
      gymLoading,
      isDark:       mode === 'dark',
      isGym:        brand === 'gym',
      toggleMode,   // agora aceita o evento de clique para posicionar o ripple
      loadGymTheme,
      clearGymTheme,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme deve ser usado dentro de <ThemeProvider>');
  return ctx;
}